/**
 * `enqueueUploadImpl` — vanilla (non-hook) enqueue used by the binding
 * seam at `lib/upload-queue/upload-queue-bindings.ts`. The composition
 * root (`main.tsx`) wires it once at app boot so non-component callers
 * (zustand actions, react-query mutationFns) can drive the global queue
 * without calling React hooks.
 *
 * It lives in `lib/upload-queue/` rather than inside the
 * `features/upload-queue` slice because it is a composition-root adapter,
 * not a feature API — FSD-wise it belongs next to the binding seam it
 * serves.
 *
 * It returns the canonical `EnqueueUploadHandle` from
 * `./upload-queue-bindings.ts` rather than declaring its own result
 * interface: the binding seam owns the type, so the impl and the seam
 * cannot drift out of sync and silently drop a field.
 *
 * This adapter reaches into the `features/upload-queue` slice via deep
 * imports (`@/features/upload-queue/...`) — that's intentional: it IS
 * the binding seam's wire to the feature, and the seam's whole purpose
 * is to invert that direction for everyone else. The fsd-boundaries
 * test for `features/upload-queue` validates the OUTGOING direction
 * (the slice has no value imports into other features), not the
 * INCOMING composition-root adapter.
 */
import { sideTable } from '@/features/upload-queue/model/side-table';
import { useUploadQueueStore } from '@/features/upload-queue/model/upload-queue.store';
import type { EnqueueInput } from '@/features/upload-queue/model/upload-queue.store.types';
import type { EnqueueUploadHandle } from './upload-queue-bindings';

export function enqueueUploadImpl(payload: EnqueueInput): EnqueueUploadHandle {
  const { enqueue, updateProgress, markSuccess, markFailed } = useUploadQueueStore.getState();
  const id = enqueue(payload);
  const signal = sideTable.getSignal(id);
  if (!signal) {
    // Should never happen — enqueue always attaches a fresh controller.
    // Throw loudly so a regression in the store is caught instantly.
    throw new Error(`[upload-queue] enqueue produced id=${id} with no side-table entry`);
  }
  return {
    id,
    abortSignal: signal,
    onProgress: (loaded, total) => updateProgress(id, loaded, total),
    markSuccess: () => markSuccess(id),
    markFailed: (error) => markFailed(id, error),
  };
}
